import Service from '@ember/service';
import { QueryManager } from '@apollo/client/core/QueryManager';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import teachersQuery from 'dashboard/gql/queries/teachers.gql';
import { isDeactivated, isPro, isSoloPro } from 'dashboard/utils';
import { escapeHtml } from 'dashboard/utils/escape-html';
import {
  EXPORT_DEFAULT_ORDER_BY,
  EXPORT_PAGE_SIZE,
  exportLastActive,
  exportStatusLabel,
  fetchAllExportPages,
  showCsvDownloadToast,
} from 'dashboard/utils/export-list';
import { type IntlService } from 'ember-intl';
import type { School, Teacher } from 'dashboard/types';
import type ToastService from 'dashboard/services/toast';
import type { TeacherListModel } from 'dashboard/controllers/dashboard/account/teachers';
import type { TeacherSearchParams } from 'dashboard/routes/dashboard/account/teachers';

type TeachersQueryResult = { teachers: TeacherListModel['teachers'] };

export type ExportTeachersFilters = {
  emails: Array<string>;
  licenses: Array<string>;
  statuses: Array<string>;
};

export default class ExportTeachersService extends Service {
  @queryManager declare apollo: QueryManager<Service>;
  @service declare intl: IntlService;
  @service declare toast: ToastService;

  startExport(purchaser: School, filters: ExportTeachersFilters) {
    if (!purchaser) return;
    return this.exportTeachersTask.perform(purchaser, filters);
  }

  exportTeachersTask = task(
    { drop: true },
    async (purchaser: School, filters: ExportTeachersFilters) => {
      const searchParams = this.#buildSearchParams(purchaser, filters);

      const allTeachers = await fetchAllExportPages<Teacher>({
        toast: this.toast,
        fetchPage: async (after) => {
          // @ts-expect-error how to type apollo query responses?
          const result: TeachersQueryResult = await this.apollo.query({
            query: teachersQuery,
            variables: {
              first: EXPORT_PAGE_SIZE,
              after,
              purchaserId: purchaser.id,
              searchParams,
            },
            fetchPolicy: 'network-only',
          });
          return result.teachers;
        },
        messages: {
          start: this.intl.t('Exporting teachers…'),
          progress: (count, total) =>
            this.intl.t('Exporting teachers… {count} of {total}', {
              count,
              total,
            }),
          error: 'Error exporting teachers.',
        },
      });

      // cancelled
      if (!allTeachers) return;

      // do not export CSV if there are no teachers.
      if (allTeachers.length === 0) {
        const hasFilters =
          filters.emails.length > 0 ||
          filters.licenses.length > 0 ||
          filters.statuses.length > 0;
        this.toast.notify(
          this.intl.t(
            hasFilters
              ? 'There are no teachers with the selected filters'
              : 'There are no teachers in this school',
          ),
          'neutral',
        );
        return;
      }

      this.exportCsv(allTeachers, purchaser);
    },
  );

  exportCsv(teachers: Teacher[], purchaser: School) {
    const isDistrict = purchaser.type === 'District';

    // match the column order of the teachers table
    const header = isDistrict
      ? [
          this.intl.t('First Name'),
          this.intl.t('Last Name'),
          this.intl.t('Email'),
          this.intl.t('School'),
          this.intl.t('License'),
        ]
      : [
          this.intl.t('First Name'),
          this.intl.t('Last Name'),
          this.intl.t('Email'),
          this.intl.t('Last active'),
          this.intl.t('Status'),
          this.intl.t('License'),
        ];
    const rows = teachers.map((teacher) => this.#buildRow(teacher, isDistrict));

    // escape the school name since it's directly inserted, just in case
    const safeName = escapeHtml(purchaser.name);

    showCsvDownloadToast(
      [header, ...rows],
      `${safeName} - Teachers.csv`,
      this.intl.t('{name} - Teachers.csv', { name: safeName }),
    );
  }

  // match the filter logic of the teachers route
  #buildSearchParams(
    purchaser: School,
    { emails, licenses, statuses }: ExportTeachersFilters,
  ): TeacherSearchParams {
    const searchParams: TeacherSearchParams = {
      purchaserId: purchaser.id,
      orderBy: EXPORT_DEFAULT_ORDER_BY,
    };

    // don't send `emailVerified` if both (or neither) are selected
    if (emails.length === 1) {
      searchParams.emailVerified = emails.includes('confirmed');
    }

    // don't send `pro` if both (or neither) are selected
    if (licenses.length === 1) {
      searchParams.pro = licenses.includes('pro');
    }

    if (statuses.length > 0) {
      searchParams.accountStatuses = statuses;
    }

    return searchParams;
  }

  #buildRow(teacher: Teacher, isDistrict: boolean): string[] {
    const firstName = teacher.firstName ?? '';
    const lastName = teacher.lastName ?? '';
    const email = teacher.email ?? '';

    // license is not shown for deactivated teachers in the table
    const license = isDeactivated(teacher)
      ? ''
      : isPro(teacher)
        ? this.intl.t('Pro')
        : isSoloPro(teacher)
          ? this.intl.t('Solo Pro')
          : this.intl.t('Basic');

    if (isDistrict) {
      return [
        firstName,
        lastName,
        email,
        teacher.linkedPurchaserName ?? '',
        license,
      ];
    }

    return [
      firstName,
      lastName,
      email,
      exportLastActive(this.intl, teacher),
      exportStatusLabel(this.intl, teacher),
      license,
    ];
  }
}

declare module '@ember/service' {
  interface Registry {
    'export-teachers': ExportTeachersService;
  }
}
